import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDigitalLibrary } from '../../contexts/DigitalLibraryContext';
import { 
  FaBook, 
  FaFileAlt, 
  FaGraduationCap, 
  FaSearch, 
  FaFilter, 
  FaDownload, 
  FaStar, 
  FaTimes, 
  FaBookmark, 
  FaRegBookmark,
  FaSort,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import SEO from '../SEO';
import './DigitalLibrary.css';

const DigitalLibrary = () => {
  const { 
    textbooks, 
    examPapers, 
    researchPapers, 
    loading, 
    error, 
    filters, 
    setFilters,
    filterResources,
    updateResource 
  } = useDigitalLibrary();

  const [activeTab, setActiveTab] = useState('textbooks');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('newest');
  const [advancedFiltersVisible, setAdvancedFiltersVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [resourceType, setResourceType] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  // Define tabs for navigation with enhanced icons
  const tabs = [
    { 
      id: 'textbooks', 
      label: 'Textbooks', 
      icon: <FaBook className="tab-icon" />,
      description: 'Access comprehensive textbooks'
    },
    { 
      id: 'examPapers', 
      label: 'Exam Papers', 
      icon: <FaFileAlt className="tab-icon" />,
      description: 'Previous exam papers and solutions'
    },
    { 
      id: 'researchPapers', 
      label: 'Research Papers', 
      icon: <FaGraduationCap className="tab-icon" />,
      description: 'Academic research and publications'
    }
  ];

  const resultsPerPage = 9;

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('digitalLibraryFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Extract unique categories from resources
  useEffect(() => {
    const allResources = [...textbooks, ...examPapers, ...researchPapers];
    const uniqueCategories = [...new Set(allResources.map(r => r.category).filter(Boolean))];
    setCategories(uniqueCategories);
  }, [textbooks, examPapers, researchPapers]);

  // Save favorites to localStorage when updated
  useEffect(() => {
    localStorage.setItem('digitalLibraryFavorites', JSON.stringify(favorites));
  }, [favorites]);

  // Reset to first page when filters or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeTab, searchQuery, sortOption]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const toggleFavorite = (resourceId) => {
    if (favorites.includes(resourceId)) {
      setFavorites(favorites.filter(id => id !== resourceId));
    } else {
      setFavorites([...favorites, resourceId]);
    }
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
    setShowDetailsModal(true);
  };

  const handleDownload = async (resourceId, resourceType) => {
    // Increment download count
    try {
      const currentResource = filterResources(resourceType, {}).find(r => r.id === resourceId);
      if (currentResource) {
        await updateResource(resourceType, resourceId, {
          downloads: (currentResource.downloads || 0) + 1
        });
      }
    } catch (error) {
      console.error("Error updating download count:", error);
    }
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // Change handler functions for the filter dropdown
  const handleResourceTypeChange = (e) => {
    const type = e.target.value;
    setResourceType(type);
    
    // If "all" is selected, don't filter by type, otherwise set the active tab to the selected type
    if (type === 'all') {
      // Keep the current active tab but reset other type filters
      setFilters({
        ...filters,
        type: ''
      });
    } else {
      // Map the dropdown value to the corresponding tab id
      const tabMap = {
        'textbook': 'textbooks',
        'exam': 'examPapers',
        'research': 'researchPapers'
      };
      setActiveTab(tabMap[type] || 'textbooks');
      
      // Update filters
      setFilters({
        ...filters,
        type: type
      });
    }
  };

  // Change handler for the sort dropdown
  const handleSortByChange = (e) => {
    const sort = e.target.value;
    setSortBy(sort);
    
    // Map the dropdown value to the corresponding sort option
    const sortMap = {
      'date': 'newest',
      'title': 'title',
      'downloads': 'downloads',
      'rating': 'rating'
    };
    
    setSortOption(sortMap[sort] || 'newest');
  };

  const renderResourceCard = (resource) => (
    <motion.div 
      className="resource-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.03 }}
      onClick={() => handleResourceClick(resource)}
    >
      <div className="resource-header">
        <h3>{resource.title}</h3>
        <div className="resource-meta">
          <span className="author">{resource.author}</span>
          <span className="date">{new Date(resource.uploadDate).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="resource-body">
        <p className="description">{resource.description.substring(0, 100)}...</p>
        <div className="resource-stats">
          <span><FaDownload /> {resource.downloads}</span>
          <span><FaStar /> {resource.rating}</span>
          {resource.category && (
            <span className="category-tag">{resource.category}</span>
          )}
        </div>
      </div>
      <div className="resource-footer">
        <a 
          href={resource.fileUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="download-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleDownload(resource.id, activeTab);
          }}
        >
          Download
        </a>
        <button 
          className="favorite-btn"
          onClick={(e) => {
            e.stopPropagation(); 
            toggleFavorite(resource.id);
          }}
        >
          {favorites.includes(resource.id) ? <FaBookmark /> : <FaRegBookmark />}
        </button>
      </div>
    </motion.div>
  );

  // Filter and sort resources
  const processResources = () => {
    let filtered = filterResources(activeTab, filters).filter(resource => 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (resource.author && resource.author.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Apply sorting
    switch(sortOption) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.uploadDate) - new Date(b.uploadDate));
        break;
      case 'downloads':
        filtered.sort((a, b) => (b.downloads || 0) - (a.downloads || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        break;
    }

    return filtered;
  };

  const paginatedResources = () => {
    const filtered = processResources();
    const startIndex = (currentPage - 1) * resultsPerPage;
    return filtered.slice(startIndex, startIndex + resultsPerPage);
  };

  const totalPages = Math.ceil(processResources().length / resultsPerPage);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading">Loading resources...</div>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <>
      <SEO 
        title="Digital Library - Student Helper"
        description="Access Ethiopian textbooks, exam papers, and research papers in one place."
        keywords="digital library, Ethiopian textbooks, exam papers, research papers, academic resources"
      />
      <div className="digital-library">
        <motion.div 
          className="library-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>Digital Library</h1>
          <p>Access Ethiopian academic resources in one place</p>
        </motion.div>

        <div className="library-controls">
          <div className="search-filter-container">
            <div className="search-bar">
              <span className="search-icon"><FaSearch /></span>
              <input 
                type="text" 
                placeholder="Search resources..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <select 
              className="filter-dropdown"
              value={resourceType}
              onChange={(e) => handleResourceTypeChange(e)}
            >
              <option value="all">All Resources</option>
              <option value="textbook">Textbooks</option>
              <option value="exam">Exam Papers</option>
              <option value="research">Research Papers</option>
            </select>
            
            <select 
              className="filter-dropdown"
              value={sortBy}
              onChange={(e) => handleSortByChange(e)}
            >
              <option value="date">Most Recent</option>
              <option value="title">Title (A-Z)</option>
              <option value="downloads">Most Downloaded</option>
              <option value="rating">Highest Rating</option>
            </select>
          </div>

          <motion.div 
            className="library-tabs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {tabs.map(tab => (
              <motion.button 
                key={tab.id}
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </motion.button>
            ))}
            {favorites.length > 0 && (
              <motion.button 
                className={activeTab === 'favorites' ? 'active' : ''}
                onClick={() => setActiveTab('favorites')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaBookmark className="tab-icon"/> 
                <span>Favorites</span>
              </motion.button>
            )}
          </motion.div>
        </div>

        <div className="resources-grid">
          {paginatedResources().map(resource => renderResourceCard(resource))}
        </div>

        {processResources().length === 0 && (
          <div className="no-results">
            <p>No resources found matching your criteria.</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              <FaChevronLeft /> Previous
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next <FaChevronRight />
            </button>
          </div>
        )}

        <AnimatePresence>
          {showDetailsModal && selectedResource && (
            <motion.div 
              className="resource-details-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}></div>
              <motion.div 
                className="modal-content"
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 50, opacity: 0 }}
              >
                <button className="close-modal" onClick={() => setShowDetailsModal(false)}>
                  <FaTimes />
                </button>
                
                <div className="resource-details-header">
                  <h2>{selectedResource.title}</h2>
                  <button 
                    className="favorite-btn-large"
                    onClick={() => toggleFavorite(selectedResource.id)}
                  >
                    {favorites.includes(selectedResource.id) ? (
                      <><FaBookmark /> Remove from Favorites</>
                    ) : (
                      <><FaRegBookmark /> Add to Favorites</>
                    )}
                  </button>
                </div>
                
                <div className="resource-details-meta">
                  <p><strong>Author:</strong> {selectedResource.author}</p>
                  <p><strong>Published:</strong> {new Date(selectedResource.uploadDate).toLocaleDateString()}</p>
                  <p><strong>University:</strong> {selectedResource.university || 'Not specified'}</p>
                  <p><strong>Subject:</strong> {selectedResource.subject}</p>
                  {selectedResource.category && (
                    <p><strong>Category:</strong> {selectedResource.category}</p>
                  )}
                  <p><strong>Language:</strong> {
                    selectedResource.language === 'en' ? 'English' :
                    selectedResource.language === 'am' ? 'Amharic' :
                    selectedResource.language === 'or' ? 'Oromiffa' :
                    selectedResource.language === 'ti' ? 'Tigrinya' : 
                    selectedResource.language
                  }</p>
                </div>
                
                <div className="resource-details-description">
                  <h3>Description</h3>
                  <p>{selectedResource.description}</p>
                </div>
                
                <div className="resource-details-stats">
                  <div className="stat">
                    <FaDownload />
                    <span>{selectedResource.downloads} Downloads</span>
                  </div>
                  <div className="stat">
                    <FaStar />
                    <span>{selectedResource.rating} Rating</span>
                  </div>
                </div>
                
                <div className="resource-details-actions">
                  <a 
                    href={selectedResource.fileUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="download-btn-large"
                    onClick={() => handleDownload(selectedResource.id, activeTab)}
                  >
                    <FaDownload /> Download Resource
                  </a>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default DigitalLibrary; 